/* eslint-disable no-console */
import _ from "lodash";
import numeral from "numeral";
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp
} from "firebase/firestore";

function getQualifierValue(header, value) {
  const { qualifierFormat, qualifierComp } = header;
  if (
    qualifierFormat === "boolean" ||
    qualifierFormat === "yesno" ||
    qualifierFormat === "truefalse"
  ) {
    const valLowerCase = value.toLowerCase();
    if (valLowerCase === "yes" || valLowerCase === "true") {
      return true;
    }
    return false;
  } else if (qualifierFormat === "number") {
    const valNumber = parseFloat(value.toString().replace(/[^0-9.-]/g, ""));
    const comparing = qualifierComp || 100;
    return valNumber >= comparing;
  }
  return false;
}

function getQualifierImg(header, value) {
  const qualifierValue = getQualifierValue(header, value);
  if (qualifierValue) {
    return "tick.svg";
  }
  return "cross.svg";
}

function getColumnValue(
  qualifierHeaders,
  accountLabel,
  avatar,
  firstname,
  lastname,
  firstLastInitial,
  row,
  rowIndex,
  header
) {
  const { type, column, format, rounding } = header;

  if (column === "Icon") {
    let allQualified = true;

    qualifierHeaders.forEach(qHeader => {
      allQualified =
        allQualified && getQualifierValue(qHeader, row[qHeader.column] || "");
    });

    return allQualified;
  } else if (column === "Rank") {
    return rowIndex + 1;
  } else if (column === "Avatar") {
    return avatar;
  } else if (column === "First Name + Last Initial") {
    return firstLastInitial;
  } else if (column === "First Name") {
    return firstname;
  } else if (column === "Last Name") {
    return lastname;
  } else if (column === "Company Title" || column === "Full Name") {
    return accountLabel;
  } else if (type === "qualifier") {
    return getQualifierImg(header, row[column] || "");
  } else if (type !== "number") {
    return row[column];
  } else {
    const numberValue = row[column] || "";
    const numberString = numberValue.toString().replace(/[^0-9.-]/g, "");
    const rounded = numeral(numberString).format(rounding);
    const isAppend = format !== "%";
    return isAppend ? `${format}${rounded}` : `${rounded}${format}`;
  }
}

const state = {
  leaderboards: [],
  leaderboardsRaw: [],
  loadingLeaderboards: false,
  loadingCurrentLeaderboard: false,
  currentLeaderboard: null,
  currentLeaderboardRaw: {},
  pendingLeaderboardUpdates: {},
  pendingLeaderboardRawUpdates: {},
  error: null
};

const actions = {
  async loadLeaderboards({ commit, getters }) {
    commit("setLoadingLeaderboards", true);
    commit("setLeaderboards", []);
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardsRef = collection(programRef, "leaderboards");
      const q = query(leaderboardsRef, orderBy("titleUppercase"));
      const querySnapshot = await getDocs(q);

      const leaderboards = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        leaderboards.push({
          id: docSnapshot.id,
          ...data,
          selectedDatabucket: data.selectedDatabucket || "",
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setLeaderboards", leaderboards);
      return leaderboards;
    } catch (error) {
      console.error("Error loading leaderboards:", error);
      commit("setError", error.message);
      commit("setLeaderboards", []);
      throw error;
    } finally {
      commit("setLoadingLeaderboards", false);
    }
  },

  async loadLeaderboardsRaw({ commit, getters }) {
    commit("setLeaderboardsRaw", []);
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardsRawRef = collection(programRef, "leaderboardsRaw");
      const querySnapshot = await getDocs(leaderboardsRawRef);

      const leaderboardsRaw = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        leaderboardsRaw.push({
          id: docSnapshot.id,
          csvName: data.csvName,
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setLeaderboardsRaw", leaderboardsRaw);
      return leaderboardsRaw;
    } catch (error) {
      console.error("Error loading raw leaderboards:", error);
      commit("setError", error.message);
      commit("setLeaderboardsRaw", []);
      throw error;
    }
  },

  async loadLeaderboardsByDatabucket({ getters, commit }, databucketId) {
    if (!databucketId) {
      console.error("Databucket ID is required");
      return;
    }

    commit("setLeaderboards", []);
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardsRef = collection(programRef, "leaderboards");
      const q = query(
        leaderboardsRef,
        where("selectedDatabucket", "==", databucketId)
      );
      const querySnapshot = await getDocs(q);

      const leaderboards = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        leaderboards.push({
          ...data,
          id: docSnapshot.id,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      commit("setLeaderboards", leaderboards);
      return leaderboards;
    } catch (error) {
      console.error("Error loading leaderboards by databucket:", error);
      commit("setError", error.message);
      commit("setLeaderboards", []);
      throw error;
    }
  },

  async loadCurrentLeaderboard({ commit, getters }, leaderboardId) {
    if (!leaderboardId) {
      throw new Error("Leaderboard ID is required");
    }

    commit("setLoadingCurrentLeaderboard", true);
    commit("setCurrentLeaderboard", null);
    commit("setCurrentLeaderboardRaw", {});
    commit("clearLeaderboardPendingUpdates");
    commit("clearLeaderboardRawPendingUpdates");
    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRef = doc(
        collection(programRef, "leaderboards"),
        leaderboardId
      );
      const leaderboardSnapshot = await getDoc(leaderboardRef);

      if (!leaderboardSnapshot.exists()) {
        throw new Error("No such leaderboard exists");
      }

      const leaderboardData = leaderboardSnapshot.data();

      // Note: Give default values if it doesn't exist
      // It's necessary in store level for deep comparison of pending updates
      const leaderboard = {
        ...leaderboardData,
        id: leaderboardSnapshot.id,
        participantType: leaderboardData.participantType || "member",
        mode: leaderboardData.mode || "dynamic",
        order: leaderboardData.order || 0,
        selectedFilters: leaderboardData.selectedFilters || [],
        filteredEntityMap: leaderboardData.filteredEntityMap || [],
        homeMappings: leaderboardData.homeMappings || {},
        remarkMappings: leaderboardData.remarkMappings || [],
        headerMappings: leaderboardData.headerMappings || [],
        iconMappings: leaderboardData.iconMappings || {},
        columnMappings: leaderboardData.columnMappings || [],
        rankMappings: leaderboardData.rankMappings || {
          sortDir: "desc",
          winRows: 1,
          maxRows: 0
        },
        created: leaderboardData.created?.toDate() || new Date(),
        updated: leaderboardData.updated?.toDate() || new Date(),
        selectedDatabucket: leaderboardData.selectedDatabucket
      };

      // Load raw data based on mode
      let leaderboardRaw = {};
      if (leaderboard.mode === "static") {
        const leaderboardRawRef = doc(
          collection(programRef, "leaderboardsRaw"),
          leaderboardId
        );
        const leaderboardRawSnapshot = await getDoc(leaderboardRawRef);

        if (leaderboardRawSnapshot.exists()) {
          const leaderboardRawData = leaderboardRawSnapshot.data();
          leaderboardRaw = {
            updated: leaderboardRawData.updated?.toDate() || new Date(),
            csvData: JSON.parse(leaderboardRawData.csvData || "[]"),
            csvName: leaderboardRawData.csvName
          };
        }
      } else {
        // Dynamic mode - load from databucket
        const databucketId = leaderboard.selectedDatabucket || "dummyId";
        const databucketRef = doc(
          db(),
          "programs",
          getters.programId,
          "databuckets",
          databucketId
        );
        const totalResultRef = doc(
          collection(databucketRef, "results"),
          "total"
        );
        const leaderboardRawSnapshot = await getDoc(totalResultRef);

        if (leaderboardRawSnapshot.exists()) {
          const leaderboardRawData = leaderboardRawSnapshot.data();
          leaderboardRaw = {
            updated: leaderboardRawData.updated?.toDate() || new Date(),
            csvData: JSON.parse(leaderboardRawData.csvData || "[]"),
            csvName: leaderboardRawData.csvName
          };
        }
      }

      commit("setCurrentLeaderboard", leaderboard);
      commit("setCurrentLeaderboardRaw", leaderboardRaw);
      return leaderboard;
    } catch (error) {
      console.error("Error loading current leaderboard:", error);
      commit("setError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentLeaderboard", false);
    }
  },

  async getLeaderboardById({ getters }, leaderboardId) {
    if (!leaderboardId) {
      console.error("Leaderboard ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRef = doc(
        collection(programRef, "leaderboards"),
        leaderboardId
      );
      const leaderboardDoc = await getDoc(leaderboardRef);

      if (leaderboardDoc.exists()) {
        const data = leaderboardDoc.data();
        return {
          id: leaderboardDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Leaderboard with ID ${leaderboardId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting leaderboard by ID:", error);
      return null;
    }
  },

  fakeLoadCurrentLeaderboard({ commit }, payload) {
    const { leaderboardData, leaderboardRawData } = payload;

    const leaderboard = {
      ...leaderboardData,
      id: leaderboardData.id,
      participantType: leaderboardData.participantType || "company",
      mode: leaderboardData.mode || "dynamic",
      order: leaderboardData.order || 0,
      selectedFilters: leaderboardData.selectedFilters || [],
      filteredEntityMap: leaderboardData.filteredEntityMap || [],
      homeMappings: leaderboardData.homeMappings || {},
      remarkMappings: leaderboardData.remarkMappings || [],
      headerMappings: leaderboardData.headerMappings || [],
      iconMappings: leaderboardData.iconMappings || {},
      columnMappings: leaderboardData.columnMappings || [],
      rankMappings: leaderboardData.rankMappings || {
        sortDir: "desc",
        winRows: 1,
        maxRows: 0
      }
    };

    const leaderboardRaw = {};
    if (leaderboardRawData) {
      leaderboardRaw.csvData = JSON.parse(leaderboardRawData.csvData || "[]");
      leaderboardRaw.csvName = leaderboardRawData.csvName;
    }

    commit("setCurrentLeaderboard", leaderboard);
    commit("setCurrentLeaderboardRaw", leaderboardRaw);
  },

  async createLeaderboard({ dispatch, commit, getters }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Leaderboard data with title is required");
    }

    commit("setError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardsRef = collection(programRef, "leaderboards");

      // Check for duplicate titles
      const titleUppercase = payload.title.toUpperCase();
      const titleDupQuery = query(
        leaderboardsRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Title is already registered");
      }

      const leaderboard = {
        ...payload,
        titleUppercase,
        createdBy: getters.user?.id || "unknown",
        updatedBy: getters.user?.id || "unknown",
        created: serverTimestamp(),
        updated: serverTimestamp()
      };

      const newLeaderboardRef = await addDoc(leaderboardsRef, leaderboard);

      // Note: server time is unavailable until we refetch.
      const tmpLeaderboard = {
        ...leaderboard,
        id: newLeaderboardRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createLeaderboard", tmpLeaderboard);
      dispatch("setSnackbar", "Leaderboard created successfully");
      return tmpLeaderboard;
    } catch (error) {
      console.error("Error creating leaderboard:", error);
      commit("setError", error.message);
      throw error;
    }
  },

  async updateLeaderboard({ getters, dispatch }, { id, updates }) {
    if (!id || !updates) {
      throw new Error("Leaderboard ID and updates are required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRef = doc(collection(programRef, "leaderboards"), id);

      // Verify leaderboard exists
      const leaderboardDoc = await getDoc(leaderboardRef);
      if (!leaderboardDoc.exists()) {
        throw new Error("Leaderboard not found");
      }

      // Check for duplicate titles if title is being updated
      if (updates.title) {
        const titleUppercase = updates.title.toUpperCase();
        const leaderboardsRef = collection(programRef, "leaderboards");
        const titleDupQuery = query(
          leaderboardsRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const titleDupSnapshot = await getDocs(titleDupQuery);

        if (!titleDupSnapshot.empty) {
          let duplicated = false;
          titleDupSnapshot.forEach(docSnapshot => {
            if (docSnapshot.id !== id) {
              duplicated = true;
            }
          });
          if (duplicated) {
            throw new Error("Leaderboard title already exists");
          }
        }
        updates.titleUppercase = titleUppercase;
      }

      const updateData = {
        ...updates,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "unknown"
      };

      await updateDoc(leaderboardRef, updateData);
      dispatch("setSnackbar", "Leaderboard updated successfully");
      return true;
    } catch (error) {
      console.error("Error updating leaderboard:", error);
      throw error;
    }
  },

  async updateCurrentLeaderboard({ getters, dispatch }) {
    const {
      hasLeaderboardPendingUpdates,
      hasLeaderboardRawPendingUpdates
    } = getters;

    if (hasLeaderboardRawPendingUpdates) {
      try {
        await Promise.all([
          dispatch("syncLeaderboard"),
          dispatch("syncLeaderboardRaw")
        ]);
      } catch (error) {
        console.error("Error updating leaderboard with raw data:", error);
        throw error;
      }
    }

    if (hasLeaderboardPendingUpdates && !hasLeaderboardRawPendingUpdates) {
      try {
        await dispatch("syncLeaderboard");
      } catch (error) {
        console.error("Error updating leaderboard:", error);
        throw error;
      }
    }

    dispatch("setSnackbar", "Leaderboard saved successfully");
  },

  async syncLeaderboard({ commit, getters, state }) {
    const clientNow = new Date();

    const payload = {
      ...state.pendingLeaderboardUpdates,
      remarkData: getters.leaderboardRemarkData,
      headerData: getters.leaderboardHeaderData,
      columnData: getters.leaderboardColumnData
    };

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRef = doc(
        collection(programRef, "leaderboards"),
        state.currentLeaderboard.id
      );

      await updateDoc(leaderboardRef, {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "unknown"
      });

      commit("patchCurrentLeaderboard", {
        updated: clientNow
      });
      commit("clearLeaderboardPendingUpdates");
    } catch (error) {
      console.error("Error syncing leaderboard:", error);
      throw new Error("Error when syncing leaderboard");
    }
  },

  async syncLeaderboardRaw({ commit, getters, state }) {
    const clientNow = new Date();

    // Note: When fetching data from databucket, there is no need
    // to update the leaderboard raw.
    if (state.currentLeaderboard.mode === "dynamic") {
      commit("clearLeaderboardRawPendingUpdates");
      return;
    }

    const payload = {
      ...state.pendingLeaderboardRawUpdates
    };

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRawRef = doc(
        collection(programRef, "leaderboardsRaw"),
        state.currentLeaderboard.id
      );

      await setDoc(
        leaderboardRawRef,
        {
          ...payload,
          csvData: JSON.stringify(payload.csvData),
          updated: serverTimestamp()
        },
        { merge: true }
      );

      commit("patchCurrentLeaderboardRaw", {
        updated: clientNow
      });
      commit("clearLeaderboardRawPendingUpdates");
    } catch (error) {
      console.error("Error syncing leaderboard raw:", error);
      throw new Error("Error when syncing leaderboard raw");
    }
  },

  async deleteLeaderboard({ commit, getters, state }) {
    const currentLeaderboardId = state.currentLeaderboard.id;

    if (!currentLeaderboardId) {
      throw new Error("No current leaderboard to delete");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const leaderboardRef = doc(
        collection(programRef, "leaderboards"),
        currentLeaderboardId
      );
      const leaderboardRawRef = doc(
        collection(programRef, "leaderboardsRaw"),
        currentLeaderboardId
      );

      await Promise.all([
        deleteDoc(leaderboardRef),
        deleteDoc(leaderboardRawRef)
      ]);

      commit("deleteLeaderboard", currentLeaderboardId);
      return true;
    } catch (error) {
      console.error("Error deleting leaderboard:", error);
      throw new Error("Error when deleting leaderboard");
    }
  },

  async selectDatabucket({ getters, commit }, databucketId) {
    if (!databucketId) {
      throw new Error("Databucket ID is required");
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const databucketRef = doc(
        collection(programRef, "databuckets"),
        databucketId
      );
      const totalResultRef = doc(collection(databucketRef, "results"), "total");
      const totalResultSnapshot = await getDoc(totalResultRef);

      if (!totalResultSnapshot.exists()) {
        throw new Error("Databucket result not found");
      }

      const data = totalResultSnapshot.data();

      commit("patchCurrentLeaderboard", {
        selectedDatabucket: databucketId
      });
      commit("patchCurrentLeaderboardRaw", {
        csvData: JSON.parse(data.csvData || "[]"),
        csvName: data.csvName,
        updated: data.updated?.toDate() || new Date()
      });
    } catch (error) {
      console.error("Error selecting databucket:", error);
      throw error;
    }
  },

  // Note: This is only updating the store, not persisting with db
  patchCurrentLeaderboard({ commit }, payload) {
    commit("patchCurrentLeaderboard", payload);
  },

  // Note: This is only updating the store, not persisting with db
  patchCurrentLeaderboardRaw({ commit }, payload) {
    commit("patchCurrentLeaderboardRaw", payload);
  },

  clearLeaderboardError({ commit }) {
    commit("setError", null);
  }
};

const mutations = {
  setLeaderboards(state, payload) {
    state.leaderboards = payload;
  },

  setLeaderboardsRaw(state, payload) {
    state.leaderboardsRaw = payload;
  },

  setLoadingLeaderboards(state, payload) {
    state.loadingLeaderboards = payload;
  },

  setCurrentLeaderboard(state, payload) {
    state.currentLeaderboard = payload;
  },

  setCurrentLeaderboardRaw(state, payload) {
    state.currentLeaderboardRaw = payload;
  },

  setLoadingCurrentLeaderboard(state, payload) {
    state.loadingCurrentLeaderboard = payload;
  },

  setError(state, payload) {
    state.error = payload;
  },

  createLeaderboard(state, payload) {
    state.leaderboards = [...state.leaderboards, payload];
  },

  patchCurrentLeaderboard(state, payload) {
    state.currentLeaderboard = {
      ...state.currentLeaderboard,
      ...payload
    };

    state.pendingLeaderboardUpdates = {
      ...state.pendingLeaderboardUpdates,
      ...payload
    };
  },

  patchCurrentLeaderboardRaw(state, payload) {
    state.currentLeaderboardRaw = {
      ...state.currentLeaderboardRaw,
      ...payload
    };

    state.pendingLeaderboardRawUpdates = {
      ...state.pendingLeaderboardRawUpdates,
      ...payload
    };
  },

  clearLeaderboardPendingUpdates(state) {
    state.pendingLeaderboardUpdates = {};
  },

  clearLeaderboardRawPendingUpdates(state) {
    state.pendingLeaderboardRawUpdates = {};
  },

  deleteLeaderboard(state, payload) {
    state.leaderboards = state.leaderboards.filter(item => item.id !== payload);
  }
};

const getters = {
  leaderboards(state) {
    return state.leaderboards;
  },

  leaderboardsRaw(state) {
    return state.leaderboardsRaw;
  },

  loadingLeaderboards(state) {
    return state.loadingLeaderboards;
  },

  loadingCurrentLeaderboard(state) {
    return state.loadingCurrentLeaderboard;
  },

  currentLeaderboard(state) {
    return state.currentLeaderboard;
  },

  currentLeaderboardRaw(state) {
    return state.currentLeaderboardRaw;
  },

  leaderboardError(state) {
    return state.error;
  },

  getLeaderboardById: state => id => {
    return (
      state.leaderboards.find(leaderboard => leaderboard.id === id) || null
    );
  },

  getLeaderboardByTitle: state => title => {
    const titleUppercase = title.toUpperCase();
    return (
      state.leaderboards.find(
        leaderboard => leaderboard.titleUppercase === titleUppercase
      ) || null
    );
  },

  leaderboardsCount(state) {
    return state.leaderboards.length;
  },

  hasLeaderboards(state) {
    return state.leaderboards.length > 0;
  },

  leaderboardEntityMap(state, getters) {
    const leaderboard = getters.currentLeaderboard;
    if (!leaderboard) return [];

    if (leaderboard.participantType === "company") {
      if (leaderboard.selectedFilters.length > 0) {
        return leaderboard.filteredEntityMap;
      } else {
        return getters.companyAccountKeysMap || {};
      }
    } else if (leaderboard.participantType === "member") {
      if (leaderboard.selectedFilters.length > 0) {
        return leaderboard.filteredEntityMap;
      } else {
        return getters.memberAccountKeysMap || {};
      }
    } else {
      return {};
    }
  },

  leaderboardCsvHeaders(state) {
    const { csvData } = state.currentLeaderboardRaw;
    if (!csvData || !csvData.length) {
      return [];
    }
    return csvData[0] || [];
  },

  leaderboardCsvBody(state, getters) {
    const { csvData } = state.currentLeaderboardRaw;
    if (!csvData || !csvData.length) {
      return [];
    }

    return csvData.slice(1).map(row => {
      return getters.leaderboardCsvHeaders.reduce(
        (rowResult, header, headerIndex) => {
          return {
            ...rowResult,
            [header]: row[headerIndex]
          };
        },
        {}
      );
    });
  },

  leaderboardSystemHeaders(state) {
    const calculated = ["Rank", "Icon"];

    if (!state.currentLeaderboard) return calculated;

    return state.currentLeaderboard.participantType === "member"
      ? [
          "Full Name",
          "First Name",
          "Last Name",
          "Avatar",
          "First Name + Last Initial",
          ...calculated
        ]
      : ["Company Title", ...calculated];
  },

  leaderboardMatchingData(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard || !currentLeaderboard.accountKeyColumn) {
      return [];
    }

    const { accountKeyColumn } = currentLeaderboard;
    const { leaderboardCsvBody, leaderboardEntityMap } = getters;

    if (!leaderboardCsvBody.length) {
      return [];
    }

    return leaderboardCsvBody.filter(item => {
      const accountKeyValue = item[accountKeyColumn];
      return !!(accountKeyValue && leaderboardEntityMap[accountKeyValue]);
    });
  },

  leaderboardAccountsMap(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard || !currentLeaderboard.accountKeyColumn) {
      return [];
    }

    const { accountKeyColumn } = currentLeaderboard;
    const { leaderboardMatchingData, leaderboardEntityMap } = getters;

    if (!leaderboardMatchingData.length) {
      return [];
    }

    const allAccountKeyValues = leaderboardMatchingData.map(
      item => item[accountKeyColumn]
    );
    const uniqAccountKeyValues = _.uniq(allAccountKeyValues);

    return uniqAccountKeyValues.map(item => {
      const entityData = leaderboardEntityMap[item] || {};
      const label = entityData.title || item;

      return {
        label,
        value: item,
        ...entityData
      };
    });
  },

  leaderboardSortData(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard || !currentLeaderboard.rankMappings) {
      return getters.leaderboardMatchingData;
    }

    const {
      rankMappings: { sortColumn, sortDir }
    } = currentLeaderboard;

    if (!sortColumn) {
      return getters.leaderboardMatchingData;
    }

    // Note: Concat is for preventing side effect
    // JavaScript sort function mutates additional array and
    // concat helps you to copy the array before sorting
    return getters.leaderboardMatchingData.concat().sort((rowA, rowB) => {
      const valueA = parseFloat(rowA[sortColumn] || 0);
      const valueB = parseFloat(rowB[sortColumn] || 0);
      return sortDir === "asc" ? valueA - valueB : valueB - valueA;
    });
  },

  leaderboardColumnData(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard) {
      return [];
    }

    const {
      rankMappings,
      columnMappings,
      accountKeyColumn
    } = currentLeaderboard;
    const { leaderboardSortData, leaderboardEntityMap } = getters;

    if (!columnMappings || !Array.isArray(columnMappings)) {
      return [];
    }

    const qualifierHeaders = columnMappings.filter(
      item => item.type === "qualifier"
    );

    const subData = rankMappings?.maxRows
      ? leaderboardSortData.slice(0, rankMappings.maxRows)
      : leaderboardSortData;

    const data = subData.map((row, rowIndex) => {
      const entityData = leaderboardEntityMap[row[accountKeyColumn]] || {};

      const columnDataItem = {
        id: entityData.id || row[accountKeyColumn],
        accountKey: row[accountKeyColumn]
      };

      const accountLabel = entityData.title || "";
      const avatar = entityData.avatar || "";
      const firstLastInitial = entityData.firstLastInitial || "";
      const firstname = entityData.firstname || "";
      const lastname = entityData.lastname || "";

      const columnDataItemValues = {};
      columnMappings.forEach(header => {
        const columnDataItemValue = getColumnValue(
          qualifierHeaders,
          accountLabel,
          avatar,
          firstname,
          lastname,
          firstLastInitial,
          row,
          rowIndex,
          header
        );
        columnDataItemValues[header.uid] = columnDataItemValue;
      });
      columnDataItem.values = columnDataItemValues;
      return columnDataItem;
    });

    const iconColumn = columnMappings.find(header => header.column === "Icon");
    if (iconColumn && rankMappings?.winRows) {
      let winningRows = 0;
      data.forEach(row => {
        if (row.values[iconColumn.uid] && winningRows < rankMappings.winRows) {
          row.isWinner = true;
          winningRows += 1;
        }
      });
    }

    return data;
  },

  leaderboardHeaderData(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard) {
      return [];
    }

    const {
      rankMappings,
      headerMappings,
      columnMappings,
      accountKeyColumn
    } = currentLeaderboard;
    const { leaderboardSortData, leaderboardEntityMap } = getters;

    if (
      !headerMappings ||
      !Array.isArray(headerMappings) ||
      !columnMappings ||
      !Array.isArray(columnMappings)
    ) {
      return [];
    }

    const qualifierHeaders = columnMappings.filter(
      item => item.type === "qualifier"
    );

    const data = leaderboardSortData.map((row, rowIndex) => {
      const entityData = leaderboardEntityMap[row[accountKeyColumn]] || {};

      const headerDataItem = {
        id: entityData.id || row[accountKeyColumn],
        accountKey: row[accountKeyColumn]
      };

      const accountLabel = entityData.title || "";
      const avatar = entityData.avatar || "";
      const firstLastInitial = entityData.firstLastInitial || "";
      const firstname = entityData.firstname || "";
      const lastname = entityData.lastname || "";

      const headerDataItemValues = {};

      // Note: Use qualifier columns from column mappings;
      headerMappings.concat(qualifierHeaders).forEach(header => {
        const headerDataItemValue = getColumnValue(
          qualifierHeaders,
          accountLabel,
          avatar,
          firstname,
          lastname,
          firstLastInitial,
          row,
          rowIndex,
          header
        );
        headerDataItemValues[header.uid] = headerDataItemValue;
      });
      headerDataItem.values = headerDataItemValues;
      return headerDataItem;
    });

    const iconColumn = headerMappings.find(header => header.column === "Icon");
    if (iconColumn && rankMappings?.winRows) {
      let winningRows = 0;
      data.forEach(row => {
        if (row.values[iconColumn.uid] && winningRows < rankMappings.winRows) {
          row.isWinner = true;
          winningRows += 1;
        }
      });
    }

    return data;
  },

  leaderboardRemarkData(state, getters) {
    const currentLeaderboard = state.currentLeaderboard;
    if (!currentLeaderboard) {
      return [];
    }

    const { remarkMappings, accountKeyColumn } = currentLeaderboard;
    const { leaderboardSortData, leaderboardEntityMap } = getters;

    if (!remarkMappings || !Array.isArray(remarkMappings)) {
      return [];
    }

    const data = leaderboardSortData.map(row => {
      const entityData = leaderboardEntityMap[row[accountKeyColumn]] || {};

      const remarkDataItem = {
        id: entityData.id || row[accountKeyColumn],
        accountKey: row[accountKeyColumn]
      };
      const remarkDataItemValues = {};

      remarkMappings.forEach(header => {
        const remarkDataItemValue = row[header.column];
        remarkDataItemValues[header.uid] = remarkDataItemValue;
      });
      remarkDataItem.values = remarkDataItemValues;
      return remarkDataItem;
    });

    return data;
  },

  hasLeaderboardPendingUpdates(state) {
    return Object.keys(state.pendingLeaderboardUpdates).length !== 0;
  },

  hasLeaderboardRawPendingUpdates(state) {
    return Object.keys(state.pendingLeaderboardRawUpdates).length !== 0;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
